import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import TelegramLoginButton from '../../components/sentry/TelegramLoginButton';
import AddToHomeScreenBanner from '../../components/sentry-pwa/AddToHomeScreenBanner';
import {
  ensurePwaWallet,
  exchangeTelegramHandoff,
  fetchAgentAuthUser,
  fetchPwaAuthUser,
  loginAgentAccount,
  loginPwaAccount,
  loginTelegram,
  registerAgentComplete,
  registerAgentInit,
  resetAgentPassword,
  registerPwaAccount,
  setAgentSession,
  setPwaSession,
  setTelegramSession,
  type TelegramAuthPayload,
  type PwaAuthUser,
  type TelegramUser
} from '../../utils/telegramAuth';

const WalletConnectionContainer = styled.div<{ $fullWidth?: boolean }>`
  height: 100vh;
  width: 100%;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  overflow-y: auto;
  position: relative;
  padding: 40px 20px 40px 20px;
  box-sizing: border-box;
  
  /* Desktop layout */
  @media (min-width: 768px) {
    ${props => props.$fullWidth ? css`
      max-width: 720px;
      width: 100%;
      margin: 0 auto;
      border-radius: 24px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: #0b0b12;
    ` : css`
      max-width: 414px;
      width: 414px;
      margin: 0 auto;
      box-shadow: 0 0 40px rgba(0, 0, 0, 0.8), 
                  0 0 0 12px rgba(0, 0, 0, 0.5),
                  0 0 0 16px #333333;
      border-radius: 24px;
      left: 50%;
      transform: translateX(-50%);
      position: relative;
      z-index: 1;
    `}
  }
`;

const LogoWrapper = styled.div`
  margin-top: 24px;
`;

const GoldLogo = styled.img`
  width: 220px;
  height: 220px;
  object-fit: contain;
  animation: logoPulse 2.4s ease-in-out infinite;

  @keyframes logoPulse {
    0%,
    100% {
      opacity: 0.9;
    }
    50% {
      opacity: 1;
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 300px;
  margin-bottom: 40px;
`;

const HandoffButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`;

const TelegramButtonShell = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const AccountInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 14px;
`;

const AccountButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid rgba(16, 185, 129, 0.4);
  background: rgba(16, 185, 129, 0.14);
  color: #10b981;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 360px;
  background: #0b0b0f;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  cursor: pointer;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
`;

const HelperText = styled.p`
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #f87171;
`;

const StatusText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-align: center;
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Tagline = styled.p`
  color: #71717a;
  font-size: 14px;
  text-align: center;
  margin-bottom: 32px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  max-width: 280px;
`;

const InstallHintWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const parseTelegramPayloadFromUrl = (): TelegramAuthPayload | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const id = params.get('id');
  const hash = params.get('hash');
  const authDate = params.get('auth_date');
  if (!id || !hash || !authDate) return null;
  return {
    id: Number(id),
    hash,
    auth_date: Number(authDate),
    first_name: params.get('first_name') || undefined,
    last_name: params.get('last_name') || undefined,
    username: params.get('username') || undefined,
    photo_url: params.get('photo_url') || undefined
  };
};

const normalizeAgentNameInput = (value: string) => {
  const lowered = value.trim().toLowerCase();
  const normalized = lowered
    .replace(/-/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized;
};

interface WalletConnectionScreenProps {
  onProceed: () => void;
  onAgentProceed?: () => void;
  allowTelegram?: boolean;
  defaultToAccountModal?: boolean;
  fullWidth?: boolean;
  accountButtonLabel?: string;
  agentButtonLabel?: string;
  showAgentLogin?: boolean;
}

const SentryWalletConnectionScreen: React.FC<WalletConnectionScreenProps> = ({
  onProceed,
  onAgentProceed,
  allowTelegram = true,
  defaultToAccountModal = false,
  fullWidth = false,
  accountButtonLabel = 'Use a Sentry Account',
  agentButtonLabel = 'Agent Login 🦞',
  showAgentLogin = true
}) => {
  const [telegramUser, setTelegramUser] = useState<PwaAuthUser | TelegramUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [handoffCode, setHandoffCode] = useState<string | null>(null);
  const [handoffStatus, setHandoffStatus] = useState<string | null>(null);
  const [accountUsername, setAccountUsername] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accountCreateMode, setAccountCreateMode] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [agentPassword, setAgentPassword] = useState('');
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentCreateMode, setAgentCreateMode] = useState(false);
  const [agentAlreadyRegistered, setAgentAlreadyRegistered] = useState(false);
  const [agentRegistrationUrl, setAgentRegistrationUrl] = useState<string | null>(null);
  const [agentSessionId, setAgentSessionId] = useState<string | null>(null);
  const passwordMeetsRequirement = accountPassword.length >= 8;
  const agentPasswordMeetsRequirement = agentPassword.length >= 8;
  const telegramBotUsername = (import.meta.env as any).VITE_TELEGRAM_BOT_USERNAME as string | undefined;
  const prefersHandoff = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (!allowTelegram) return false;
    const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
    const iosStandalone = (window.navigator as any).standalone === true;
    return Boolean(displayModeStandalone || iosStandalone);
  }, [allowTelegram]);

  useEffect(() => {
    if (defaultToAccountModal) {
      setAccountModalOpen(true);
    }
  }, [defaultToAccountModal]);

  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      try {
        if (typeof window !== 'undefined') {
          const storedHandoff = window.localStorage.getItem('telegram_handoff_code');
          if (storedHandoff) {
            setHandoffCode(storedHandoff);
            setHandoffStatus('Waiting for Telegram login to complete...');
          }
        }

        if (allowTelegram) {
          const urlPayload = parseTelegramPayloadFromUrl();
          if (urlPayload) {
            const user = await loginTelegram(urlPayload);
            if (!cancelled) {
              setPwaSession(null);
              setAgentSession(null);
              setTelegramUser(user);
              await ensurePwaWallet();
              window.history.replaceState({}, '', window.location.pathname);
              onProceed();
              return;
            }
          }
        }

        const user = await fetchPwaAuthUser();
        if (cancelled) return;
        if (user) {
          setTelegramUser(user);
          await ensurePwaWallet();
          onProceed();
          return;
        }

        const agentUser = await fetchAgentAuthUser();
        if (cancelled) return;
        if (agentUser) {
          if (onAgentProceed) {
            onAgentProceed();
          } else {
            window.location.href = '/sentry-agent/wallet';
          }
        }
      } catch {
        if (!cancelled) {
          setTelegramUser(null);
        }
      }
    };

    loadSession();

    return () => {
      cancelled = true;
    };
  }, [onProceed]);

  useEffect(() => {
    if (!allowTelegram) return;
    if (!handoffCode) return;
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const sessionId = await exchangeTelegramHandoff(handoffCode);
        if (!sessionId || cancelled) {
          return;
        }
        setTelegramSession(sessionId);
        setPwaSession(null);
        setAgentSession(null);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('telegram_handoff_code');
        }
        setHandoffCode(null);
        setHandoffStatus(null);
        const user = await fetchPwaAuthUser();
        if (!user || cancelled) return;
        setTelegramUser(user);
        await ensurePwaWallet();
        onProceed();
      } catch {
        // Ignore polling failures
      }
    };

    const interval = window.setInterval(() => {
      attempts += 1;
      if (attempts > 90) {
        setHandoffStatus('Still waiting for Telegram login. Try again if needed.');
        window.clearInterval(interval);
        return;
      }
      poll();
    }, 2000);

    poll();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [allowTelegram, handoffCode, onProceed]);

  const handleTelegramAuth = async (payload: TelegramAuthPayload) => {
    setLoading(true);
    setError(null);
    try {
      const user = await loginTelegram(payload);
      setPwaSession(null);
      setAgentSession(null);
      setTelegramUser(user);
      await ensurePwaWallet();
      onProceed();
    } catch (authError: any) {
      setError(authError?.message || 'Telegram login failed.');
    } finally {
      setLoading(false);
    }
  };

  const startHandoffLogin = () => {
    if (typeof window === 'undefined') return;
    const code =
      (window.crypto as Crypto | undefined)?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem('telegram_handoff_code', code);
    setHandoffCode(code);
    setHandoffStatus('Complete Telegram login in the browser, then return here.');
    const url = `/sentry-pwa/auth?handoff=${encodeURIComponent(code)}`;
    const opened = window.open(url, '_blank');
    if (!opened) {
      window.location.href = url;
    }
  };

  const handleAccountRegister = async () => {
    setAccountLoading(true);
    setAccountStatus(null);
    setError(null);
    if (!passwordMeetsRequirement) {
      setAccountStatus('Password must be at least 8 characters.');
      setAccountLoading(false);
      return;
    }
    try {
      const user = await registerPwaAccount({
        username: accountUsername.trim(),
        password: accountPassword
      });
      setTelegramSession(null);
      setAgentSession(null);
      setTelegramUser(user);
      await ensurePwaWallet();
      setAccountStatus('Save your login info securely. Losing it may result in losing access to your account and assets.');
      setTimeout(() => {
        setAccountModalOpen(false);
        onProceed();
      }, 1500);
    } catch (accountError: any) {
      setAccountStatus(accountError?.message || 'Account creation failed.');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleAccountLogin = async () => {
    setAccountLoading(true);
    setAccountStatus(null);
    setError(null);
    try {
      const user = await loginPwaAccount({
        username: accountUsername.trim(),
        password: accountPassword
      });
      setTelegramSession(null);
      setAgentSession(null);
      setTelegramUser(user);
      await ensurePwaWallet();
      setAccountModalOpen(false);
      onProceed();
    } catch (accountError: any) {
      setAccountStatus(accountError?.message || 'Account login failed.');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleAgentRegisterInit = async () => {
    setAgentLoading(true);
    setAgentStatus(null);
    setError(null);
    setAgentAlreadyRegistered(false);
    try {
      const normalizedName = normalizeAgentNameInput(agentName);
      if (!normalizedName) {
        setAgentStatus('Enter a valid agent name (letters, numbers, underscore).');
        return;
      }
      if (normalizedName !== agentName.trim()) {
        setAgentName(normalizedName);
      }
      const result = await registerAgentInit({ agentName: normalizedName });
      if (result.status === 'already_registered') {
        setAgentAlreadyRegistered(true);
        setAgentRegistrationUrl(null);
        setAgentSessionId(null);
        setAgentStatus('Agent already registered. Reset the password below.');
        return;
      }
      if (!result.sessionId || !result.registrationUrl) {
        setAgentStatus('Failed to start verification. Try again.');
        return;
      }
      setAgentSessionId(result.sessionId);
      setAgentRegistrationUrl(result.registrationUrl);
      setAgentStatus('Open the verification link to continue, then click Complete.');
      const opened = window.open(result.registrationUrl, '_blank');
      if (!opened) {
        window.location.href = result.registrationUrl;
      }
    } catch (agentError: any) {
      setAgentStatus(agentError?.message || 'Agent verification init failed.');
    } finally {
      setAgentLoading(false);
    }
  };

  const handleAgentRegisterComplete = async () => {
    setAgentLoading(true);
    setAgentStatus(null);
    setError(null);
    if (!agentSessionId) {
      setAgentStatus('Missing verification session. Start verification first.');
      setAgentLoading(false);
      return;
    }
    if (!agentPasswordMeetsRequirement) {
      setAgentStatus('Password must be at least 8 characters.');
      setAgentLoading(false);
      return;
    }
    try {
      const normalizedName = normalizeAgentNameInput(agentName);
      if (!normalizedName) {
        setAgentStatus('Enter a valid agent name (letters, numbers, underscore).');
        return;
      }
      if (normalizedName !== agentName.trim()) {
        setAgentName(normalizedName);
      }
      const result = await registerAgentComplete({
        agentName: normalizedName,
        password: agentPassword,
        sessionId: agentSessionId
      });
      if (result.status !== 'ok') {
        setAgentStatus('Verification still pending. Complete the link first.');
        return;
      }
      const sessionId = result.data?.sessionId;
      if (sessionId) {
        setAgentSession(sessionId);
      } else {
        await loginAgentAccount({
          agentName: normalizedName,
          password: agentPassword
        });
      }
      setTelegramSession(null);
      setPwaSession(null);
      setAgentModalOpen(false);
      setAgentAlreadyRegistered(false);
      if (onAgentProceed) {
        onAgentProceed();
      } else {
        window.location.href = '/sentry-agent/wallet';
      }
    } catch (agentError: any) {
      setAgentStatus(agentError?.message || 'Agent registration failed.');
    } finally {
      setAgentLoading(false);
    }
  };

  const handleAgentResetPassword = async () => {
    setAgentLoading(true);
    setAgentStatus(null);
    setError(null);
    if (!agentPasswordMeetsRequirement) {
      setAgentStatus('Password must be at least 8 characters.');
      setAgentLoading(false);
      return;
    }
    try {
      const normalizedName = normalizeAgentNameInput(agentName);
      if (!normalizedName) {
        setAgentStatus('Enter a valid agent name (letters, numbers, underscore).');
        return;
      }
      if (normalizedName !== agentName.trim()) {
        setAgentName(normalizedName);
      }
      const result = await resetAgentPassword({
        agentName: normalizedName,
        password: agentPassword
      });
      if (result.status !== 'ok') {
        setAgentStatus('Password reset failed. Try again.');
        return;
      }
      const sessionId = result.data?.sessionId;
      if (sessionId) {
        setAgentSession(sessionId);
      } else {
        await loginAgentAccount({
          agentName: normalizedName,
          password: agentPassword
        });
      }
      setTelegramSession(null);
      setPwaSession(null);
      setAgentModalOpen(false);
      setAgentAlreadyRegistered(false);
      if (onAgentProceed) {
        onAgentProceed();
      } else {
        window.location.href = '/sentry-agent/wallet';
      }
    } catch (agentError: any) {
      setAgentStatus(agentError?.message || 'Password reset failed.');
    } finally {
      setAgentLoading(false);
    }
  };

  const handleAgentLogin = async () => {
    setAgentLoading(true);
    setAgentStatus(null);
    setError(null);
    try {
      const normalizedName = normalizeAgentNameInput(agentName);
      if (!normalizedName) {
        setAgentStatus('Enter a valid agent name (letters, numbers, underscore).');
        return;
      }
      if (normalizedName !== agentName.trim()) {
        setAgentName(normalizedName);
      }
      await loginAgentAccount({
        agentName: normalizedName,
        password: agentPassword
      });
      setTelegramSession(null);
      setPwaSession(null);
      setAgentModalOpen(false);
      if (onAgentProceed) {
        onAgentProceed();
      } else {
        window.location.href = '/sentry-agent/wallet';
      }
    } catch (agentError: any) {
      setAgentStatus(agentError?.message || 'Agent login failed.');
    } finally {
      setAgentLoading(false);
    }
  };

  const showInstallHint = allowTelegram;

  return (
    <WalletConnectionContainer $fullWidth={fullWidth}>
      <LogoWrapper>
        <GoldLogo src="/Sentry-Gold-Gradient.svg" alt="Sentry Gold Gradient" />
      </LogoWrapper>
      <BottomSection>
        <Tagline>
          {allowTelegram
            ? 'Sign in with Telegram or create an account to access your Sentry trading wallet'
            : 'Create or sign in to your Sentry Account to access your trading wallet'}
        </Tagline>
        {showInstallHint && (
          <InstallHintWrapper>
            <AddToHomeScreenBanner />
          </InstallHintWrapper>
        )}
        <ButtonsContainer>
          {allowTelegram && (
            <TelegramButtonShell>
              {prefersHandoff ? (
                <HandoffButton type="button" onClick={startHandoffLogin}>
                  Open Telegram Login
                </HandoffButton>
              ) : (
                <TelegramLoginButton
                  botUsername={telegramBotUsername}
                  onAuth={handleTelegramAuth}
                  size="large"
                  label="Login with Telegram"
                />
              )}
            </TelegramButtonShell>
          )}
          <AccountButton type="button" onClick={() => setAccountModalOpen(true)}>
            {accountButtonLabel}
          </AccountButton>
          {showAgentLogin && (
            <AccountButton type="button" onClick={() => setAgentModalOpen(true)}>
              {agentButtonLabel}
            </AccountButton>
          )}
          {loading && <StatusText>Signing in…</StatusText>}
          {error && <StatusText>{error}</StatusText>}
          {allowTelegram && handoffStatus && <StatusText>{handoffStatus}</StatusText>}
          {telegramUser && (
            <StatusText>
              Connected as {telegramUser.username ? `@${telegramUser.username}` : 'user'}
            </StatusText>
          )}
        </ButtonsContainer>
      </BottomSection>
      {accountModalOpen && (
        <ModalBackdrop onClick={() => setAccountModalOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Sentry Account</ModalTitle>
              <CloseButton type="button" onClick={() => setAccountModalOpen(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            <AccountInput
              type="text"
              placeholder="Username"
              value={accountUsername}
              onChange={(event) => setAccountUsername(event.target.value)}
            />
            <AccountInput
              type="password"
              placeholder="Password"
              value={accountPassword}
              onChange={(event) => setAccountPassword(event.target.value)}
            />
            <CheckboxRow>
              <input
                type="checkbox"
                checked={accountCreateMode}
                onChange={(event) => setAccountCreateMode(event.target.checked)}
              />
              Create a new Sentry Account
            </CheckboxRow>
            {accountCreateMode && (
              <>
                <HelperText>Password must be at least 8 characters.</HelperText>
                {!passwordMeetsRequirement && accountPassword.length > 0 && (
                  <ErrorText>Password does not meet the requirements (min 8 characters).</ErrorText>
                )}
              </>
            )}
            <AccountButton
              type="button"
              onClick={accountCreateMode ? handleAccountRegister : handleAccountLogin}
              disabled={accountLoading}
            >
              {accountLoading ? 'Processing…' : accountCreateMode ? 'Create' : 'Login'}
            </AccountButton>
            {accountStatus && (
              <StatusText style={{ color: accountStatus.includes('Save your login info') ? '#fbbf24' : undefined }}>
                {accountStatus}
              </StatusText>
            )}
          </ModalCard>
        </ModalBackdrop>
      )}
      {agentModalOpen && (
        <ModalBackdrop onClick={() => setAgentModalOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Agent Login</ModalTitle>
              <CloseButton type="button" onClick={() => setAgentModalOpen(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            <AccountInput
              type="text"
              placeholder="Agent name"
              value={agentName}
              onChange={(event) => setAgentName(event.target.value)}
            />
            <AccountInput
              type="password"
              placeholder="Password"
              value={agentPassword}
              onChange={(event) => setAgentPassword(event.target.value)}
            />
            <CheckboxRow>
              <input
                type="checkbox"
                checked={agentCreateMode}
                onChange={(event) => {
                  setAgentCreateMode(event.target.checked);
                  setAgentAlreadyRegistered(false);
                  setAgentRegistrationUrl(null);
                  setAgentSessionId(null);
                }}
              />
              Create a new Agent
            </CheckboxRow>
            {agentCreateMode && (
              <>
                <HelperText>Password must be at least 8 characters.</HelperText>
                {!agentPasswordMeetsRequirement && agentPassword.length > 0 && (
                  <ErrorText>Password does not meet the requirements (min 8 characters).</ErrorText>
                )}
              </>
            )}
            {agentCreateMode ? (
              <>
                <AccountButton type="button" onClick={handleAgentRegisterInit} disabled={agentLoading}>
                  {agentLoading ? 'Starting…' : 'Start Verification'}
                </AccountButton>
                {agentRegistrationUrl && (
                  <HelperText>Complete ClawKey (VeryAI) verification, then finish registration.</HelperText>
                )}
                {agentSessionId && (
                  <AccountButton type="button" onClick={handleAgentRegisterComplete} disabled={agentLoading}>
                    {agentLoading ? 'Finishing…' : 'Complete Verification'}
                  </AccountButton>
                )}
                {agentAlreadyRegistered && (
                  <AccountButton type="button" onClick={handleAgentResetPassword} disabled={agentLoading}>
                    {agentLoading ? 'Resetting…' : 'Reset Password'}
                  </AccountButton>
                )}
              </>
            ) : (
              <AccountButton type="button" onClick={handleAgentLogin} disabled={agentLoading}>
                {agentLoading ? 'Processing…' : 'Login'}
              </AccountButton>
            )}
            {agentStatus && (
              <StatusText style={{ color: agentStatus.includes('Save your') ? '#fbbf24' : undefined }}>
                {agentStatus}
              </StatusText>
            )}
          </ModalCard>
        </ModalBackdrop>
      )}
    </WalletConnectionContainer>
  );
};

export default SentryWalletConnectionScreen;
